import React, { useState, useEffect } from 'react';

// Progressive image component to handle dynamic replacement, loading, fallback, and error states
export const DynamicImage: React.FC<{
  primarySrc: string
  fallbackSrc: string
  alt: string
  altKey?: string
  width?: number
  height?: number
  className?: string
}> = ({ primarySrc, fallbackSrc, alt, altKey, className }) => {
  const [src, setSrc] = useState<string>(primarySrc)
  const [state, setState] = useState<"loading" | "ready" | "error">("loading")

  useEffect(() => {
    let cancelled = false
    setState("loading")
    setSrc(primarySrc)
    const img = new Image()
    img.src = primarySrc
    img.onload = () => {
      if (cancelled) return
      setState("ready")
    }
    img.onerror = () => {
      if (cancelled) return
      const fb = new Image()
      fb.src = fallbackSrc
      fb.onload = () => {
        if (cancelled) return
        setSrc(fallbackSrc)
        setState("ready")
      }
      fb.onerror = () => {
        if (cancelled) return
        setState("error")
      }
    }
    return () => {
      cancelled = true
    }
  }, [primarySrc, fallbackSrc])

  if (state === "error") {
    // Return empty transparent div to let parent background show through
    // aria-label ensures accessibility without visible text
    return (
      <div
        role="img"
        aria-label={alt || "Image not available"}
        className={`w-full h-full rounded-md ${className || ""}`}
      />
    )
  }

  return (
    <div className={`relative overflow-hidden ${className || ""}`} style={{ width: '100%', height: '100%' }}>
      {state === "loading" && (
        <div aria-busy="true" className="absolute inset-0 bg-gray-100 animate-pulse" />
      )}
      <img
        src={src}
        alt={alt}
        data-alt-key={altKey}
        className={`w-full h-full object-cover transition-opacity duration-500 ${state === "ready" ? "opacity-100" : "opacity-0"}`}
      />
    </div>
  )
}
